import { getDeliveryByEventLabel, getProductIdFromProductCode } from '@noths/polaris-client-utils';
import type { BaseFieldObject } from '@noths/polaris-dev-ts-types';

import type { Product } from 'src/redux/products/types/Product';
import type { ImpressionFieldObject, ProductFieldObject } from 'src/types/googleAnalytics';
import { PageType } from 'src/types/navigation';
import { isCategoryPageGuard, isPartnerPageGuard, isSearchPageGuard } from 'src/utils/typeGuards';
import { logger } from './serverLogger';

type FieldObjectType<P extends PageType> = ProductFieldObject<P> | ImpressionFieldObject<P>;

const generateFieldObject = (
  product: Product | undefined,
  items: Record<number, Product[]>,
  productsReturnedCount: number,
): BaseFieldObject => {
  if (!product) {
    logger.warn('Product is required for generateFieldObject');
    return {} as BaseFieldObject;
  }

  const {
    code,
    deliveryByEvent,
    deliveryCost,
    isNew,
    onSale,
    partner,
    price,
    purchasable,
    salePercentage,
    title,
  } = product;
  const findProductByCode = (prod: Product) => prod.code === product.code;
  const productsArr = Object.values(items).flat();
  const pageProductPosition =
    (productsArr.findIndex(findProductByCode) % productsReturnedCount) + 1;

  const findProductWithinPage = (pageNumber: string) => items[pageNumber].find(findProductByCode);
  const pages = Object.keys(items);
  const pageNumber = Number(pages.find(findProductWithinPage));

  /*
    Use page product position if on the 1st page.
    Otherwise, count number of products spanning all previous pages,
    and add page product position to said number.
  */
  const dimension25 =
    pageNumber === 1
      ? pageProductPosition
      : pageProductPosition + (pageNumber - 1) * productsReturnedCount;

  const dimension22List: string[] = [
    deliveryCost !== null && 'free uk delivery',
    isNew && 'new',
    onSale && !!salePercentage && `-${salePercentage}% off`,
    !purchasable && 'out of stock',
    deliveryByEvent?.arriveByEvent && getDeliveryByEventLabel(deliveryByEvent.eventName),
  ].filter(Boolean) as string[];

  return {
    brand: partner.name,
    dimension22: dimension22List.join(','),
    dimension25: dimension25,
    dimension30: String(code),
    dimension31: String(price.amount / 100),
    dimension32: String(price.amount / 100),
    dimension33: onSale ? `${salePercentage}.0%` : 'none',
    dimension34: partner.shortcode,
    id: String(getProductIdFromProductCode(code)),
    name: title,
    position: pageProductPosition,
    price: String(price.amount / 100),
  };
};

const getDimension16PartialFieldObject = (
  fieldObject: Partial<BaseFieldObject>,
  product: Product | undefined,
  pageType: PageType | null,
  categoryId?: number,
) => {
  if (!product) {
    logger.warn('Product is required for getDimension16PartialFieldObject');
    return {} as Partial<BaseFieldObject>;
  }

  const fieldObj: Partial<BaseFieldObject> = {};

  if (isCategoryPageGuard<FieldObjectType<PageType.Category>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = String(categoryId);
  } else if (isPartnerPageGuard<FieldObjectType<PageType.Partner>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = product.partner?.name?.toLowerCase();
  } else if (isSearchPageGuard<FieldObjectType<PageType.Search>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = '15000';
  }

  return fieldObj;
};

export const getProductFieldObject = <T extends PageType>(
  product: Product | undefined,
  items: Record<number, Product[]>,
  productsReturnedCount: number,
  categoryId: number | undefined,
  pageType: PageType | null,
): ProductFieldObject<T> => {
  if (!product) {
    logger.warn('Product is required for getProductFieldObject');
    return {} as ProductFieldObject<T>;
  }

  const fieldObject = generateFieldObject(product, items, productsReturnedCount);

  const dimension16PartialFieldObject = getDimension16PartialFieldObject(
    fieldObject,
    product,
    pageType,
    categoryId,
  ) as ProductFieldObject<T>;

  return {
    ...fieldObject,
    ...dimension16PartialFieldObject,
  } as ProductFieldObject<T>;
};

export const getFieldObjectList = (
  product: Product | undefined,
  searchTerm: string,
  pageType: PageType | null,
  listSuffix?: ` - ${string}`,
  partnerName?: string,
) => {
  if (!product) {
    logger.warn('Product is required for getFieldObjectList');
    return '';
  }

  switch (pageType) {
    case PageType.Category:
      return `${PageType.Category} - ${listSuffix || ''}`;
    case PageType.Partner:
      return `${PageType.Partner} - ${(partnerName || '').toLowerCase()}${listSuffix || ''}`;
    case PageType.Search:
      return `${PageType.Search} - ${searchTerm}${listSuffix || ''}`;
    default:
      return '';
  }
};

export const getImpressionFieldObject = <T extends PageType>(
  product: Product | undefined,
  items: Record<number, Product[]>,
  productsReturnedCount: number,
  searchTerm: string,
  pageType: PageType | null,
  categoryId: number | undefined,
  {
    listSuffix,
    partnerName,
  }: {
    listSuffix?: ` - ${string}`;
    partnerName?: string;
  } = {},
): ImpressionFieldObject<T> => {
  if (!product) {
    logger.warn('Product is required for getImpressionFieldObject');
    return {} as ImpressionFieldObject<T>;
  }

  const impressionFieldObject = {
    ...generateFieldObject(product, items, productsReturnedCount),
    list: getFieldObjectList(product, searchTerm, pageType, listSuffix, partnerName),
  };

  const dimension16PartialFieldObject = getDimension16PartialFieldObject(
    impressionFieldObject,
    product,
    pageType,
    categoryId,
  ) as ImpressionFieldObject<T>;

  return {
    ...impressionFieldObject,
    ...dimension16PartialFieldObject,
  } as ImpressionFieldObject<T>;
};
